// Màn "Câu đã sai" — danh sách câu đã trả lời sai và chưa làm lại đúng.
//
// Nguồn: GET /api/v1/wrong-questions (đã có sẵn ở internal/api/wrong.go, từ
// lâu mà chưa ai gọi — trước đây màn Tiến độ hiện dòng "TODO: chưa có
// endpoint", tức là nói dối người dùng rằng app chưa làm được, trong khi nó
// có).
//
// Bấm một dòng thì luyện lại ĐÚNG câu đó: POST /sessions nhận `question_ids`
// nên không phải lọc theo đề mục rồi chờ may mắn gặp đúng câu.

import { useCallback, useEffect, useState } from 'react'
import { api } from '../api'
import type { WrongQuestion, WrongTopicCount } from '../types'
import { ApiError } from '../types'
import { Between, Label, Note, Screen, Shell } from '../components/shell'
import { Bar, TopBar } from '../components/ui'
import { Icon } from '../components/Icon'

/** Số câu mỗi lần tải. Danh sách có thể dài hàng trăm dòng nên phân trang;
 *  50 là đủ để lướt mà không phải chờ. */
const PAGE = 50

export function WrongView({
  onBack,
  onPractice,
}: {
  onBack: () => void
  onPractice: (ids: string[]) => void
}) {
  const [items, setItems] = useState<WrongQuestion[]>([])
  const [byTopic, setByTopic] = useState<WrongTopicCount[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  // '' = tất cả. Dùng slug rỗng của đề mục "Tất cả" làm việc này rồi.
  const [topic, setTopic] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    api
      .wrongQuestions(topic, PAGE)
      .then((r) => {
        setItems(r.items)
        setTotal(r.total)
        setErr('')
        // Biểu đồ luôn vẽ theo TẤT CẢ đề mục, kể cả khi đang lọc. Lọc rồi mà
        // biểu đồ chỉ còn một cột thì mất hết ý nghĩa so sánh — và tự nó biến
        // mất thông tin về đề mục mạnh nhất để so với đề mục đang xem.
        if (!topic) setByTopic(r.by_topic)
      })
      .catch((e) => setErr((e as ApiError).detail || 'Không tải được danh sách.'))
      .finally(() => setLoading(false))
  }, [topic])

  useEffect(load, [load])

  // Chia cho giá trị lớn nhất, không chia cho tổng: cột ngắn nhất phải vẫn đủ
  // thấy, và người đọc so được với cột lớn nhất chứ không phải với tổng.
  const peakTopic = byTopic.length > 0 ? Math.max(...byTopic.map((t) => t.count)) : 1

  return (
    <Shell>
      <Screen>
        <TopBar title={loading && total === 0 ? 'Câu đã sai' : `Câu đã sai (${total})`} onBack={onBack} />

        {/* BIỂU ĐỒ SỐ CÂU SAI THEO ĐỀ MỤC.
            Danh sách phẳng dài 104 dòng thì không ai nhìn ra mình yếu hệ nào —
            phải tự đếm. Đây là thứ cần thấy trước, nên nó nằm TRÊN danh sách.
            Bấm một cột thì lọc luôn, biến biểu đồ thành bộ lọc. */}
        {byTopic.length > 0 && (
          <div className="card mb-3">
            <Label className="mb-2">Sai nhiều nhất theo đề mục</Label>
            <div className="space-y-2.5">
              {byTopic.map((t) => {
                const on = topic === t.topic
                return (
                  <button
                    key={t.topic}
                    onClick={() => setTopic(on ? '' : t.topic)}
                    aria-pressed={on}
                    className="block w-full cursor-pointer text-left"
                  >
                    <Between className="mb-1 gap-2">
                      {/* `min-w-0` + truncate: tên đề mục dài ("Hệ sinh dục, Vú và
                          Sức khoẻ sinh sản") đẩy hết số sang vòng dưới nếu không
                          co lại. */}
                      <span
                        className={
                          'min-w-0 truncate text-sm ' + (on ? 'font-semibold' : '')
                        }
                      >
                        {t.topic_name || t.topic}
                      </span>
                      <span className="shrink-0 font-mono text-sm font-semibold">
                        {t.count}
                      </span>
                    </Between>
                    <Bar
                      percent={(t.count / peakTopic) * 100}
                      tone={on ? 'bg-accent' : 'bg-bad-soft'}
                      // Số câu đã hiện bằng CHỮ ngay phía trên (dòng 95), nên
                      // thanh không mang thông tin gì thêm. Để nó là
                      // progressbar thì thành thanh không tên — người đọc màn
                      // hình nghe "progressbar 47%" mà không biết của cái gì.
                      decorative
                    />
                  </button>
                )
              })}
            </div>
            {byTopic.length > 1 && (
              <Note className="mt-2.5">
                Bấm vào một đề mục để chỉ xem câu sai của đề mục đó.
              </Note>
            )}
          </div>
        )}

        {err && <Note className="mb-3 text-bad">{err}</Note>}

        {loading && items.length === 0 && <Note>Đang tải…</Note>}

        {!loading && items.length === 0 && (
          <Note>
            {err
              ? ''
              : topic
                ? 'Không có câu sai nào ở đề mục này.'
                : 'Chưa có câu sai nào. Làm bài vài lượt rồi quay lại đây.'}
          </Note>
        )}

        <div className="divide-y divide-ink/10">
          {items.map((it) => (
            <button
              key={it.question_version_id}
              onClick={() => onPractice([it.question_version_id])}
              className="flex w-full cursor-pointer items-center gap-2.5 py-2.5 text-left"
            >
              <Icon name="x" size={15} className="shrink-0 text-bad" />
              <span className="min-w-0 flex-1">
                <span className="chip">{it.topic_name || it.topic}</span>
                {/* `truncate` + `block`: preview là chuỗi một dòng dài, không có nó
                    thì tên đề mục bị đẩy xuống hàng và cả dòng vỡ. */}
                <span className="mt-1 block truncate text-sm text-ink-soft">{it.preview}</span>
              </span>
              {it.times_wrong > 1 && (
                <span className="shrink-0 font-mono text-xs text-bad">×{it.times_wrong}</span>
              )}
              <Icon name="chevronRight" size={14} className="shrink-0 text-ink-faint" />
            </button>
          ))}
        </div>

        {total > items.length && (
          <Note className="mt-3">
            Đang hiện {items.length}/{total} câu sai nhiều nhất.
          </Note>
        )}
      </Screen>
    </Shell>
  )
}
